import React from "react";
import { Box, Chip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  formatManches,
  formatSetScore,
  isWinner,
  statusChipColor,
  statusLabel,
} from "./displayHelpers";
import PlayerIdentityLabel from "./PlayerIdentityLabel";
import { statusChipSx } from "./resultatsTheme";
import type { Game, PlayerDirectory } from "./types";
import styles from "./Resultats.module.css";

interface PoolMatchRowProps {
  game: Game;
  players: PlayerDirectory;
}

export default function PoolMatchRow({
  game,
  players,
}: PoolMatchRowProps): React.ReactElement {
  const theme = useTheme();
  const manches = formatManches(game);
  const score = formatSetScore(game);
  const slots =
    game.players.length >= 2 ? game.players : [game.players[0] ?? null, null];

  return (
    <Box className={styles.matchRow} component="article">
      <Box className={styles.matchMeta}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {game.label || `Partie ${game.order ?? game.id}`}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          color={statusChipColor(game.status)}
          label={statusLabel(game.status)}
          sx={statusChipSx(theme)}
        />
      </Box>

      <Box className={styles.matchPlayers}>
        {slots.map((licence, index) => {
          const winner = isWinner(licence, game);
          const forfeited =
            game.status === "FORFAIT" &&
            game.forfeitSlot != null &&
            game.forfeitSlot === index + 1;
          return (
            <Box
              key={`${game.id}-${index}`}
              className={`${styles.playerLine}${
                winner ? ` ${styles.playerLineWinner}` : ""
              }`}
            >
              <PlayerIdentityLabel
                licence={licence}
                directory={players}
                winner={winner}
                suffix={forfeited ? " (F)" : ""}
              />
            </Box>
          );
        })}
      </Box>

      <Box className={styles.matchScoreCol}>
        <Typography
          component="span"
          className={styles.scoreMain}
          color="text.primary"
        >
          {score}
        </Typography>
        {manches && (
          <Typography
            component="span"
            className={styles.manches}
            color="text.secondary"
          >
            {manches}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
